import Iconify from 'src/components/Iconify';
import { Autocomplete, Grid, IconButton, Stack, TextField } from '@mui/material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { useEffect, useMemo } from 'react';
import { CodeableConcept, GoalTarget } from 'src/careflow/ph-core/fhir-client/resources';
import DatePickerMoment from 'src/components/DatePickerMoment';
import moment from 'moment';
import { useCodeSystem } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { GridItem, GridSection } from 'src/components/CustomModal';
import { isEmpty } from 'lodash';

type Props = {
  target: GoalTarget[] | null;
  updateState: Function;
  i18n: i18nFunction;
  configurations: any;
}

const GoalTargets = ({ 
  target,
  updateState,
  i18n, 
  configurations 
}: Props) => {

  const [targetMeasureCS, { isFetching: isFetchingCS }] = useCodeSystem('goal-benchmark');

  const handleAddTarget = () => {
    const targets = target ?? [];
    updateState({
      target: [
        ...targets,
        {}
      ],
    })
  };

  useEffect(() => {
    if(!target || target?.length === 0){
      updateState({
        target: [{}],
      })
    }
  },[target]);

  const targetMeasureCodeableConcept = useMemo<CodeableConcept[]>(() => 
    targetMeasureCS?.concept?.map((vs) => ({
      coding: [
        {
          code: vs?.code,
          display: vs?.display,
          system: `${configurations?.baseUrl}/fhir/CodeSystem/?name=GoalBenchmark`
        }
      ]
    })) ?? [],
    [targetMeasureCS, configurations]
  );

  return (
    <Stack direction="column" ml={2} mb={2}>
      {target?.map((item: GoalTarget, index) => {
        return (
          <Grid
            container
            spacing={2}
            key={index}
            mt={index === 0 ? -4 : 0.4}
          >
            <GridItem xs={10}>
              <GridSection>
                <GridItem>
                  <Autocomplete
                    value={item?.measure ?? null}
                    disabled={isFetchingCS}
                    onChange={(_: React.SyntheticEvent, measure: CodeableConcept | null) => {
                      const newTargets = (target ?? []).map((t, i) =>
                        i === index ? { ...t, measure: measure ?? undefined } : { ...t }
                      );
                      updateState({ target: newTargets });
                    }}
                    options={targetMeasureCodeableConcept}
                    getOptionLabel={(option) => 
                      option?.coding?.[0]?.display 
                      ?? option?.coding?.[0]?.code 
                      ?? ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n('case.details.listofGoals.target.measure', 'crs')}
                        variant="outlined"
                      />
                    )}
                  />
                </GridItem>
                <GridItem>
                  <DatePickerMoment
                    label={i18n('case.details.listofGoals.target.dueDate', 'crs')}
                    value={
                      item?.dueDate && moment(item.dueDate).isValid()
                        ? moment(item.dueDate)
                        : null
                    }
                    onChange={(value) => {
                      const m = value ? moment(value) : null;
                      const newDueDate = m?.isValid() ? m.format('YYYY-MM-DD') : undefined;
                      const newTargets = (target ?? []).map((t, i) =>
                        i === index ? { ...t, dueDate: newDueDate } : { ...t }
                      );
                      updateState({ target: newTargets });
                    }}
                    onAccept={(value) => {
                      const m = value ? moment(value) : null;
                      const newDueDate = m?.isValid() ? m.format('YYYY-MM-DD') : undefined;
                      const newTargets = (target ?? []).map((t, i) =>
                        i === index ? { ...t, dueDate: newDueDate } : { ...t }
                      );
                      updateState({ target: newTargets });
                    }}
                    disabled={isFetchingCS}
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    disabled={isFetchingCS}
                    rows={4}
                    value={item?.detailString}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = !isEmpty(e?.target?.value) ? e?.target?.value : undefined;
                      const newTargets = (target ?? []).map((t, i) =>
                        i === index ? { ...t, detailString: value } : { ...t }
                      );
                      updateState({ target: newTargets });
                    }}
                    label={i18n('case.details.listofGoals.target.value', 'crs')}
                    variant="outlined"
                    placeholder={i18n('case.details.listofGoals.target.valuePlaceHolder', 'crs')}
                  />
                </GridItem>
              </GridSection>
            </GridItem>
            <GridItem xs={2} alignContent="center">
              <IconButton
                onClick={handleAddTarget}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingCS}
              >
                <Iconify icon={'eva:plus-fill'} />
              </IconButton>
              <IconButton
                onClick={() => {
                  updateState({
                    target: target?.filter((_, i) => i !== index)
                  })
                }}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={target?.length === 1 || isFetchingCS}
              >
                <Iconify icon={'eva:trash-2-outline'} />
              </IconButton>
            </GridItem>
            
          </Grid>
      )})}
    </Stack>
  );
};

export default GoalTargets;